import React, { useEffect, useMemo, useState } from "react";
import { Download, RefreshCw, Search } from "lucide-react";
import api from "../../api/api";
import Heading from "../../components/ui/Heading";
import { useAuth } from "../../context/AuthContext";
import { getSessionUsername } from "../../utils/sessionUser";

const today = () => new Date().toISOString().split("T")[0];

const firstDayOfMonth = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().split("T")[0];
};

const valueFrom = (row, keys, fallback = "") => {
  const key = keys.find((item) => row?.[item] !== undefined && row?.[item] !== null);
  return key ? row[key] : fallback;
};

const numberFrom = (row, keys) => Number(valueFrom(row, keys, 0) || 0);

const currency = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const normalizeDate = (value) => {
  if (!value) return "";
  const dateText = String(value);
  if (dateText.includes("T")) return dateText.split("T")[0];
  if (dateText.includes("/")) {
    const date = new Date(dateText);
    if (!Number.isNaN(date.getTime())) return date.toISOString().split("T")[0];
  }
  return dateText;
};

const normalizeRow = (row) => ({
  vrNo: valueFrom(row, ["VrNo", "Vrno", "VRNO", "VrId", "Vrid", "vrno"]),
  vrDate: normalizeDate(valueFrom(row, ["VrDate", "VRDate", "vrdate", "Date", "RefDate"])),
  party: valueFrom(row, ["Party", "VName", "Vname", "customer", "Customer", "Vendor", "PartyName"]),
  payMode: valueFrom(row, ["PayMode", "PAYMODE", "Mode", "PaymentMode", "paymode"]),
  bankTxnNo: valueFrom(row, ["BankTxnNo", "BankTxn", "ChequeNo", "Chqno", "TxnNo", "TransactionNo", "BankId"]),
  amount: numberFrom(row, ["Amount", "VrNetTotal", "TotPaid", "PaymentAmount", "PayAmount", "Receipt", "CDue"]),
  remarks: valueFrom(row, ["Remarks", "Remark", "Notes", "Narration"]),
});

const normalizeSummaryRow = (row) => ({
  mode: valueFrom(row, ["Mode", "PayMode", "PAYMODE"], "-"),
  transactions: numberFrom(row, ["Transactions", "Transaction", "Count"]),
  amount: numberFrom(row, ["Amount", "PaymentAmount", "TotPaid", "VrNetTotal"]),
});

const csvValue = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const ReceiptPaymentReport = () => {
  const { user } = useAuth();
  const userName = user?.UsrName || user?.username || user || getSessionUsername();
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [invoiceType, setInvoiceType] = useState("");
  const [fromDate, setFromDate] = useState(firstDayOfMonth);
  const [toDate, setToDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [generated, setGenerated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("summary");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchInvoiceTypes = async () => {
      setLoadingTypes(true);
      setError("");
      try {
        const data = await api.GetInvtype(userName);
        const types = (data?.Table || [])
          .map((item) => item.Invtype)
          .filter(Boolean);
        setInvoiceTypes(types);
        setInvoiceType((current) => current || types[0] || "");
      } catch (err) {
        console.error("Failed to load invoice types:", err);
        setInvoiceTypes([]);
        setInvoiceType("");
        setError("Failed to load invoice types.");
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchInvoiceTypes();
  }, [userName]);

  const normalizedRows = useMemo(() => rows.map(normalizeRow), [rows]);
  const normalizedSummaryRows = useMemo(() => summaryRows.map(normalizeSummaryRow), [summaryRows]);

  const filteredRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return normalizedRows;

    return normalizedRows.filter((row) =>
      [row.vrNo, row.vrDate, row.party, row.payMode, row.amount, row.remarks]
        .filter((value) => value !== undefined && value !== null)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [normalizedRows, searchTerm]);

  const filteredSummaryRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return normalizedSummaryRows;

    return normalizedSummaryRows.filter((row) =>
      [row.mode, row.transactions, row.amount]
        .filter((value) => value !== undefined && value !== null)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [normalizedSummaryRows, searchTerm]);

  const totalPaymentAmount = useMemo(
    () => filteredSummaryRows.reduce((total, row) => total + Number(row.amount || 0), 0),
    [filteredSummaryRows]
  );

  const handleGenerate = async () => {
    if (!invoiceType) {
      setError("Please select invoice type.");
      return;
    }

    setLoading(true);
    setError("");
    setGenerated(true);
    setActiveTab("summary");
    try {
      const data = await api.GetInvPaymentReceipt(invoiceType, fromDate, toDate);
      setRows(data?.Table || []);
      setSummaryRows(data?.Table1 || []);
    } catch (err) {
      console.error("Failed to load receipt/payment report:", err);
      setRows([]);
      setSummaryRows([]);
      setError("Failed to load receipt/payment report.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    const isSummary = activeTab === "summary";
    const headers = isSummary
      ? ["Pay Mode", "Transactions", "Payment Amount"]
      : ["Vr No", "Vr Date", "Party", "Pay Mode", "Amount", "Remarks"];
    const dataRows = isSummary
      ? filteredSummaryRows.map((row) => [row.mode, row.transactions, row.amount])
      : filteredRows.map((row) => [row.vrNo, row.vrDate, row.party, row.payMode, row.amount, row.remarks]);

    if (dataRows.length === 0) return;

    const csv = [headers, ...dataRows]
      .map((line) => line.map(csvValue).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `receipt-payment-${activeTab}-${fromDate}-to-${toDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <Heading>Receipt / Payment Report</Heading>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.2fr_1fr_1fr_auto] md:items-end">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Invoice Type</label>
              <select
                value={invoiceType}
                onChange={(event) => setInvoiceType(event.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white p-3 text-slate-800 outline-none"
              >
                <option value="">Select Invoice Type</option>
                {loadingTypes ? (
                  <option disabled>Loading...</option>
                ) : (
                  invoiceTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                className="w-full rounded-lg border border-slate-200 p-3 text-slate-800 outline-none"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                className="w-full rounded-lg border border-slate-200 p-3 text-slate-800 outline-none"
              />
            </div>

            <button
              type="button"
              onClick={handleGenerate}
              disabled={loading || loadingTypes || !invoiceType}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Generating" : "Generate"}
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-rose-100 bg-rose-50 p-4 font-semibold text-rose-700">
            {error}
          </div>
        )}

        {generated && (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-3 pt-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex">
                <button
                  type="button"
                  onClick={() => setActiveTab("summary")}
                  className={`rounded-t-lg px-4 py-2 text-sm font-semibold transition ${
                    activeTab === "summary"
                      ? "bg-white text-teal-700 shadow-sm"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  Summary
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("details")}
                  className={`rounded-t-lg px-4 py-2 text-sm font-semibold transition ${
                    activeTab === "details"
                      ? "bg-white text-teal-700 shadow-sm"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  Details
                </button>
              </div>

              <div className="mb-3 flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                <div className="relative w-full sm:w-80">
                  <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search report..."
                    className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-800 outline-none focus:border-teal-400"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleExportCsv}
                  disabled={loading || (activeTab === "summary" ? filteredSummaryRows.length === 0 : filteredRows.length === 0)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  CSV
                </button>
              </div>
            </div>

            {activeTab === "summary" && (
              <div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Pay Mode</th>
                        <th className="px-4 py-3 text-right font-semibold">Transactions</th>
                        <th className="px-4 py-3 text-right font-semibold">Payment Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="3" className="px-4 py-12 text-center font-semibold text-slate-500">
                            Loading summary...
                          </td>
                        </tr>
                      ) : filteredSummaryRows.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="px-4 py-12 text-center font-semibold text-slate-500">
                            No summary records found.
                          </td>
                        </tr>
                      ) : (
                        filteredSummaryRows.map((row, index) => (
                          <tr key={`${row.mode}-${index}`} className="border-b border-slate-100 last:border-b-0">
                            <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-800">{row.mode}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600">{row.transactions}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-slate-900">
                              {currency(row.amount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {filteredSummaryRows.length > 0 && !loading && (
                      <tfoot className="bg-slate-900 text-white">
                        <tr>
                          <td className="px-4 py-3 font-bold" colSpan="2">Total</td>
                          <td className="px-4 py-3 text-right font-bold">{currency(totalPaymentAmount)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            )}

            {activeTab === "details" && (
              <div>
               
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Vr No</th>
                        <th className="px-4 py-3 text-left font-semibold">Vr Date</th>
                        <th className="px-4 py-3 text-left font-semibold">Party</th>
                        <th className="px-4 py-3 text-left font-semibold">Pay Mode</th>
                    
                        <th className="px-4 py-3 text-right font-semibold">Amount</th>
                        <th className="px-4 py-3 text-left font-semibold">Remarks</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-12 text-center font-semibold text-slate-500">
                            Loading report...
                          </td>
                        </tr>
                      ) : filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-12 text-center font-semibold text-slate-500">
                            No receipt/payment records found.
                          </td>
                        </tr>
                      ) : (
                        filteredRows.map((row, index) => (
                          <tr key={`${row.vrNo || "vr"}-${index}`} className="hover:bg-emerald-50/60">
                            <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-800">{row.vrNo || "-"}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.vrDate || "-"}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-slate-800">{row.party || "-"}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.payMode || "-"}</td>
                           
                            <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-slate-900">
                              {currency(row.amount)}
                            </td>
                            <td className="px-4 py-3 text-slate-600">{row.remarks || "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceiptPaymentReport;
